/**
 * Supabase Storage API Service
 *
 * Handles all file storage operations with automatic compression and optimization.
 * Uses the compression utilities from client.ts for images and thumbnails.
 *
 * SECURITY: Uploads go through a secure serverless API endpoint that validates
 * Auth0 authentication before uploading with a service role key.
 */

import { supabase, uploadFile, compressImage, generateVideoThumbnail, type CompressionOptions } from './client';
import { getAuthHeaders } from 'src/utils/auth-headers';

/**
 * Get Auth0 access token from localStorage
 * This is set by the Auth0 SDK when the user logs in
 */
const getAuth0Token = (): string | null => {
  try {
    // Auth0 SPA SDK stores tokens in localStorage with a specific key pattern
    const keys = Object.keys(localStorage);
    const auth0Key = keys.find(key =>
      key.startsWith('@@auth0spajs@@') && key.includes('::default::')
    );

    if (auth0Key) {
      const data = JSON.parse(localStorage.getItem(auth0Key) || '{}');
      return data?.body?.access_token || null;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Secure upload through API endpoint (validates Auth0 token, uses service role key)
 */
const secureUpload = async (
  bucket: string,
  file: File,
  folder?: string
): Promise<{ success: boolean; path?: string; url?: string; error?: string }> => {
  try {
    const token = getAuth0Token();

    if (!token) {
      return { success: false, error: 'Not authenticated. Please log in.' };
    }

    // Convert file to base64
    const arrayBuffer = await file.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );

    // Generate filename with folder prefix if provided
    const filename = folder ? `${folder}/${file.name}` : file.name;

    const response = await fetch('/api/storage?action=upload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: 'upload',
        bucket,
        filename,
        contentType: file.type,
        fileData: base64,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      return { success: false, error: result.error || 'Upload failed' };
    }

    return {
      success: true,
      path: result.file.path,
      url: result.file.url,
    };
  } catch (error: any) {
    return { success: false, error: error.message || 'Upload failed' };
  }
};

// Storage bucket names
export const BUCKETS = {
  LEAD_PHOTOS: 'lead-photos',
  PROJECT_PHOTOS: 'project-photos',
  SALES_RECORDINGS: 'sales-recordings',
  DOCUMENTS: 'documents',
  AVATARS: 'avatars',
  INVENTORY: 'inventory',
} as const;

export type BucketName = typeof BUCKETS[keyof typeof BUCKETS];

// File metadata type
export interface FileMetadata {
  id: string;
  name: string;
  bucket: string;
  path: string;
  url: string;
  size: number;
  mimeType: string;
  createdAt: string;
}

// Upload result type
export interface UploadResult {
  success: boolean;
  file?: FileMetadata;
  thumbnail?: FileMetadata;
  error?: string;
}

/**
 * Generate a unique filename
 */
const generateFilename = (originalName: string, prefix?: string): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const ext = originalName.split('.').pop()?.toLowerCase() || 'file';
  const baseName = originalName.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9]/g, '_');
  const shortName = baseName.substring(0, 30);

  return prefix
    ? `${prefix}/${timestamp}_${random}_${shortName}.${ext}`
    : `${timestamp}_${random}_${shortName}.${ext}`;
};

/**
 * Get public URL for a file
 */
export const getPublicUrl = (bucket: BucketName, path: string): string => {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
};

/**
 * Get signed URL for private files (expires in 1 hour)
 */
export const getSignedUrl = async (
  bucket: BucketName,
  path: string,
  expiresIn: number = 3600
): Promise<string | null> => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn);

  if (error) {
    // console.error('Failed to create signed URL:', error);
    return null;
  }

  return data.signedUrl;
};

class StorageApi {
  /**
   * Upload an image with automatic compression
   */
  async uploadImage(
    bucket: BucketName,
    file: File,
    folder?: string,
    options?: CompressionOptions
  ): Promise<UploadResult> {
    try {
      // Use the optimized upload function from client.ts
      const result = await uploadFile(bucket, folder || '', file, {
        compress: true,
        compressionOptions: {
          maxWidth: options?.maxWidth || 1920,
          maxHeight: options?.maxHeight || 1080,
          quality: options?.quality || 0.8,
          format: options?.format || 'webp',
        },
      });

      if (!result) {
        return { success: false, error: 'Upload failed' };
      }

      return {
        success: true,
        file: {
          id: result.path,
          name: file.name,
          bucket,
          path: result.path,
          url: result.url,
          size: file.size || 0,
          mimeType: 'image/webp',
          createdAt: new Date().toISOString(),
        },
      };
    } catch (error: any) {
      // console.error('Image upload failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Upload a video with automatic thumbnail generation
   * Uses secure API endpoint that validates Auth0 authentication
   */
  async uploadVideo(
    bucket: BucketName,
    file: File,
    folder?: string,
    generateThumb: boolean = true
  ): Promise<UploadResult> {
    try {
      // Use secure upload through API endpoint
      const uploadResult = await secureUpload(bucket, file, folder);

      if (!uploadResult.success) {
        return { success: false, error: uploadResult.error };
      }

      const result: UploadResult = {
        success: true,
        file: {
          id: uploadResult.path!,
          name: file.name,
          bucket,
          path: uploadResult.path!,
          url: uploadResult.url!,
          size: file.size,
          mimeType: file.type,
          createdAt: new Date().toISOString(),
        },
      };

      // Generate and upload thumbnail
      if (generateThumb) {
        try {
          const thumbnailBlob = await generateVideoThumbnail(file);
          const thumbnailFile = new File([thumbnailBlob], 'thumbnail.webp', { type: 'image/webp' });

          const thumbResult = await secureUpload(bucket, thumbnailFile, folder);

          if (thumbResult.success) {
            result.thumbnail = {
              id: thumbResult.path!,
              name: 'thumbnail.webp',
              bucket,
              path: thumbResult.path!,
              url: thumbResult.url!,
              size: thumbnailBlob.size,
              mimeType: 'image/webp',
              createdAt: new Date().toISOString(),
            };
          }
        } catch (thumbError) {
          // Continue without thumbnail
        }
      }

      return result;
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Upload a document (PDF, etc.) without compression
   * Uses secure API endpoint that validates Auth0 authentication
   */
  async uploadDocument(
    bucket: BucketName,
    file: File,
    folder?: string
  ): Promise<UploadResult> {
    try {
      // Use secure upload through API endpoint
      const uploadResult = await secureUpload(bucket, file, folder);

      if (!uploadResult.success) {
        return { success: false, error: uploadResult.error };
      }

      return {
        success: true,
        file: {
          id: uploadResult.path!,
          name: file.name,
          bucket,
          path: uploadResult.path!,
          url: uploadResult.url!,
          size: file.size,
          mimeType: file.type,
          createdAt: new Date().toISOString(),
        },
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete a file
   * Uses secure API endpoint that validates Auth0 authentication
   */
  async deleteFile(bucket: BucketName, path: string): Promise<boolean> {
    try {
      const token = getAuth0Token();

      if (!token) {
        console.error('[Storage] Not authenticated for delete');
        return false;
      }

      const response = await fetch('/api/storage?action=delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'delete', bucket, path }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[Storage] Delete failed:', error);
        return false;
      }

      console.log(`[Storage] Deleted ${path} from ${bucket}`);
      return true;
    } catch (error) {
      console.error('[Storage] Delete error:', error);
      return false;
    }
  }

  /**
   * Delete multiple files
   */
  async deleteFiles(bucket: BucketName, paths: string[]): Promise<boolean> {
    const { error } = await supabase.storage.from(bucket).remove(paths);

    if (error) {
      // console.error('Failed to delete files:', error);
      return false;
    }

    return true;
  }

  /**
   * List files in a folder
   */
  async listFiles(
    bucket: BucketName,
    folder: string = '',
    options?: { limit?: number; offset?: number }
  ): Promise<FileMetadata[]> {
    try {
      // Use server proxy to bypass RLS (service role key)
      const params = new URLSearchParams({ bucket });
      if (folder) params.append('folder', folder);

      const response = await fetch(`/api/storage?action=list&${params.toString()}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const error = await response.json();
        console.error(`[Storage] Failed to list files in ${bucket}:`, error);
        return [];
      }

      const files = await response.json();
      console.log(`[Storage] Listed ${files?.length || 0} files in ${bucket}`);

      return (files || [])
        .filter((item: any) => item.name !== '.emptyFolderPlaceholder')
        .map((item: any) => ({
          id: item.id || item.name,
          name: item.name,
          bucket,
          path: folder ? `${folder}/${item.name}` : item.name,
          url: item.url || getPublicUrl(bucket, folder ? `${folder}/${item.name}` : item.name),
          size: item.metadata?.size || 0,
          mimeType: item.metadata?.mimetype || 'application/octet-stream',
          createdAt: item.created_at || new Date().toISOString(),
        }));
    } catch (error) {
      console.error(`[Storage] Failed to list files in ${bucket}:`, error);
      return [];
    }
  }

  /**
   * Copy a file to another location
   */
  async copyFile(
    fromBucket: BucketName,
    fromPath: string,
    toBucket: BucketName,
    toPath: string
  ): Promise<boolean> {
    const { error } = await supabase.storage.from(fromBucket).copy(fromPath, toPath);

    if (error) {
      // console.error('Failed to copy file:', error);
      return false;
    }

    return true;
  }

  /**
   * Move a file to another location
   */
  async moveFile(
    fromBucket: BucketName,
    fromPath: string,
    toBucket: BucketName,
    toPath: string
  ): Promise<boolean> {
    const { error } = await supabase.storage.from(fromBucket).move(fromPath, toPath);

    if (error) {
      // console.error('Failed to move file:', error);
      return false;
    }

    return true;
  }

  /**
   * Get file metadata
   */
  async getFileInfo(bucket: BucketName, path: string): Promise<FileMetadata | null> {
    try {
      // List the specific file to get its metadata
      const folder = path.split('/').slice(0, -1).join('/');
      const filename = path.split('/').pop() || '';

      const { data, error } = await supabase.storage.from(bucket).list(folder, {
        limit: 1,
        search: filename,
      });

      if (error || !data || data.length === 0) {
        return null;
      }

      const file = data[0];
      return {
        id: file.id || file.name,
        name: file.name,
        bucket,
        path,
        url: getPublicUrl(bucket, path),
        size: file.metadata?.size || 0,
        mimeType: file.metadata?.mimetype || 'application/octet-stream',
        createdAt: file.created_at || new Date().toISOString(),
      };
    } catch (error) {
      // console.error('Failed to get file info:', error);
      return null;
    }
  }

  /**
   * Download a file as blob
   */
  async downloadFile(bucket: BucketName, path: string): Promise<Blob | null> {
    const { data, error } = await supabase.storage.from(bucket).download(path);

    if (error) {
      // console.error('Failed to download file:', error);
      return null;
    }

    return data;
  }

  /**
   * Create a download URL that forces browser download
   */
  getDownloadUrl(bucket: BucketName, path: string, filename?: string): string {
    const publicUrl = getPublicUrl(bucket, path);
    const downloadName = filename || path.split('/').pop() || 'download';
    return `${publicUrl}?download=${encodeURIComponent(downloadName)}`;
  }
}

export const storageApi = new StorageApi();

// Helper to determine file type from extension
export const getFileType = (filename: string): 'image' | 'video' | 'document' | 'other' => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';

  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
  const videoExts = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv'];
  const docExts = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'];

  if (imageExts.includes(ext)) return 'image';
  if (videoExts.includes(ext)) return 'video';
  if (docExts.includes(ext)) return 'document';
  return 'other';
};

// Helper to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// Storage usage tracking
export interface StorageUsage {
  used: number; // bytes
  limit: number; // bytes (1GB free tier)
  percentage: number;
  buckets: {
    name: string;
    size: number;
    fileCount: number;
  }[];
}

// Supabase free tier limit: 1GB
const FREE_TIER_LIMIT = 1 * 1024 * 1024 * 1024; // 1GB in bytes

/**
 * Calculate total size of files in a bucket
 */
async function getBucketSize(bucket: BucketName): Promise<{ size: number; fileCount: number }> {
  let totalSize = 0;
  let fileCount = 0;
  let offset = 0;
  const limit = 1000;

  try {
    // Paginate through all files in the bucket
    while (true) {
      const { data, error } = await supabase.storage
        .from(bucket)
        .list('', {
          limit,
          offset,
          sortBy: { column: 'created_at', order: 'desc' },
        });

      if (error) {
        // console.error(`Error listing files in ${bucket}:`, error);
        break;
      }

      if (!data || data.length === 0) {
        break;
      }

      for (const file of data) {
        if (file.name !== '.emptyFolderPlaceholder' && file.metadata?.size) {
          totalSize += file.metadata.size;
          fileCount++;
        }
      }

      if (data.length < limit) {
        break;
      }

      offset += limit;
    }
  } catch (err) {
    // console.error(`Failed to get size for bucket ${bucket}:`, err);
  }

  return { size: totalSize, fileCount };
}

/**
 * Get storage usage across all buckets
 */
export async function getStorageUsage(): Promise<StorageUsage> {
  const bucketNames = Object.values(BUCKETS);
  const bucketStats: StorageUsage['buckets'] = [];
  let totalUsed = 0;

  // Get size for each bucket
  for (const bucketName of bucketNames) {
    try {
      const { size, fileCount } = await getBucketSize(bucketName);
      bucketStats.push({
        name: bucketName,
        size,
        fileCount,
      });
      totalUsed += size;
    } catch (err) {
      // console.warn(`Could not get stats for bucket ${bucketName}:`, err);
      bucketStats.push({
        name: bucketName,
        size: 0,
        fileCount: 0,
      });
    }
  }

  return {
    used: totalUsed,
    limit: FREE_TIER_LIMIT,
    percentage: Math.min((totalUsed / FREE_TIER_LIMIT) * 100, 100),
    buckets: bucketStats,
  };
}

/**
 * Get storage usage for a specific bucket only
 */
export async function getBucketUsage(bucket: BucketName): Promise<{
  size: number;
  fileCount: number;
  percentage: number;
}> {
  const { size, fileCount } = await getBucketSize(bucket);
  return {
    size,
    fileCount,
    percentage: Math.min((size / FREE_TIER_LIMIT) * 100, 100),
  };
}
